import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Truck, DollarSign, PhoneCall, Mountain } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT } from '../utils/i18n';
const features = [{
  icon: Truck,
  title: 'Local Trucks, Faster Delivery',
  description: 'Our fleet is 30 minutes from your driveway. Same-day and next-day, no national queue.'
}, {
  icon: DollarSign,
  title: 'Direct From the Supplier',
  description: "No broker markup, no national middleman. You pay for the rock and the run — that's it."
}, {
  icon: PhoneCall,
  title: 'Real People on the Phone',
  description: 'Call once, get the dispatcher loading your order — not a ticket queue or an AI agent.'
}, {
  icon: Mountain,
  title: 'Knows the Northwest',
  description: 'We know what NW contractors actually spec — wet-soil base, drain rock, NW bark — so you order right the first time.'
}];
const container = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function WhyUs() {
  const t = useT();
  return <section className="py-16 md:py-24 dark:bg-[#474443] transition-colors duration-300 border-y border-stone-200 dark:border-[#3f3d3c] bg-[#FCF9F2] relative overflow-hidden" id="why-us" data-id="element-1714">
      {/* Subtle decorative accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-orange/40 to-transparent" data-id="element-1715" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1716">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start" data-id="element-1717">
          {/* LEFT COLUMN — Brand statement */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="lg:col-span-4 lg:sticky lg:top-24" data-id="element-1718">
            <div className="inline-flex items-center gap-2 mb-6" data-id="element-1719">
              <MapPin className="w-3.5 h-3.5 text-brand-orange" data-id="element-1720" />
              <span className="text-[11px] font-bold text-brand-orange uppercase tracking-[0.14em]" data-id="element-1721">
                {t('why.localOwned')}
              </span>
            </div>

            <h2 className="text-3xl md:text-4xl lg:text-[2.5rem] font-heading font-extrabold text-stone-900 dark:text-white leading-[1.1] mb-6 tracking-tight" data-id="element-1722">
              We're the dispatcher.
              <br data-id="element-1723" />
              We're the driver.
              <br data-id="element-1724" />
              We're local.
            </h2>

            <p className="text-stone-600 dark:text-stone-300 md:text-lg font-medium mb-8 leading-relaxed" data-id="element-1725">
              {t('why.localBody')}
            </p>
          </motion.div>

          {/* RIGHT COLUMN — Centered icon features + pill CTA */}
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{
          once: true,
          margin: '-40px'
        }} className="lg:col-span-8" data-id="element-1726">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-id="element-1727">
              {features.map((feature, index) => <motion.div key={feature.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.08
            }} className="flex flex-col items-start text-left p-4 rounded-xl bg-white/50 dark:bg-white/[0.03] border border-stone-200/60 dark:border-white/[0.06]" data-id="element-1728">
                  <div className="w-8 h-8 bg-brand-dim/10 dark:bg-brand-dim/20 rounded-lg flex items-center justify-center mb-3 border border-brand-dim/20 dark:border-brand-dim/30" data-id="element-1729">
                    <feature.icon className="w-4 h-4 text-brand-dim" data-id="element-1730" />
                  </div>
                  <h3 className="font-heading text-sm font-bold text-brand-charcoal dark:text-white mb-1 leading-snug" data-id="element-1731">
                    {feature.title}
                  </h3>
                  <p className="text-brand-charcoal/70 dark:text-white/75 text-xs leading-relaxed" data-id="element-1732">
                    {feature.description}
                  </p>
                </motion.div>)}
            </div>

            <motion.div variants={item} className="mt-10 sm:mt-12 flex justify-center sm:justify-start" data-id="element-1733">
              <Link to="/local-vs-national" className="group inline-flex items-center gap-2 text-brand-orange hover:text-brand-orange/80 font-bold text-sm tracking-tight transition-colors" data-id="element-1734">
                <span className="border-b border-brand-orange/30 group-hover:border-brand-orange pb-0.5 transition-colors" data-id="element-1735">
                  See how we stack up vs. national marketplaces
                </span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1 shrink-0" data-id="element-1736" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}